import React, { useEffect, useState, useRef } from "react";
import { FiX, FiMapPin, FiCheck, FiHome, FiMaximize2, FiEye } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { Map, MapMarker, MarkerContent, MapControls, MarkerLabel, MapPopup } from "./ui/map";
import ModalConfirmacion from "./ModalConfirmacion";

// ====================================================================
// DATOS DE EJEMPLO — Reemplazar con la API real cuando esté disponible
// ====================================================================
const INMUEBLES_MOCK = [
  {
    id: 1,
    nombre: "AGUSTIN YAÑEZ",
    direccion: "Casas (Rumbo al Trompo Mágico)",
    tipo: "Casa",
    precio_desde: 5535.1,
    precio_hasta: 6178.71,
    clave_inmueble: "MUL0033",
    habitaciones: 2,
    superficie: 120,
    lat: 20.6969,
    lng: -103.3911,
    disponible: true,
  },
  {
    id: 2,
    nombre: "AVILA CAMACHO",
    direccion: "Calle: Rafael Camacho No. 1344 espaldas de IPEJAL",
    tipo: "Departamento",
    precio_desde: 6564.85,
    precio_hasta: 6693.61,
    clave_inmueble: "MUL0038",
    habitaciones: 2,
    superficie: 85,
    lat: 20.6736,
    lng: -103.3637,
    disponible: true,
  },
  {
    id: 3,
    nombre: "Casa Colinas de San Javier",
    direccion: "Calle Olivos #89, Col. Colinas de San Javier",
    tipo: "Casa",
    precio_renta: 15000,
    habitaciones: 4,
    superficie: 200,
    lat: 20.6585,
    lng: -103.4134,
    disponible: true,
  },
  {
    id: 4,
    nombre: "Departamento Zona Centro",
    direccion: "Calle Morelos #321, Centro Histórico",
    tipo: "Departamento",
    precio_renta: 6500,
    habitaciones: 1,
    superficie: 55,
    lat: 20.6767,
    lng: -103.3475,
    disponible: true,
  },
  {
    id: 5,
    nombre: "Casa Zapopan Norte",
    direccion: "Av. Aviación #4567, Col. San Juan de Ocotán",
    tipo: "Casa",
    precio_renta: 11000,
    habitaciones: 3,
    superficie: 150,
    lat: 20.7111,
    lng: -103.4252,
    disponible: true,
  },
];

const MAP_STYLES = {
  default: undefined,
  openstreetmap: "https://tiles.openfreemap.org/styles/bright",
  openstreetmap3d: "https://tiles.openfreemap.org/styles/liberty",
};

const formatPriceRange = (desde, hasta, precioRenta) => {
  const formatVal = (val) => typeof val === "number" ? val.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : val;
  
  if (desde || hasta) {
    const dVal = desde ? parseFloat(desde) : null;
    const hVal = hasta ? parseFloat(hasta) : null;
    
    if (dVal !== null && hVal !== null) {
      if (dVal === hVal) {
        return `$${formatVal(dVal)}`;
      }
      return `$${formatVal(dVal)} - $${formatVal(hVal)}`;
    }
    if (dVal !== null) {
      return `Desde $${formatVal(dVal)}`;
    }
    if (hVal !== null) {
      return `Hasta $${formatVal(hVal)}`;
    }
  }
  if (precioRenta) {
    return `$${formatVal(parseFloat(precioRenta))}`;
  }
  return "$5,000.00";
};

/**
 * MapaInmuebles — Modal a pantalla completa con mapa interactivo MapLibre GL (mapcn)
 */
export default function MapaInmuebles({ isOpen, onClose, onSelect, codigoAfiliado, correoElectronico, apiUrl, folio }) {
  const [inmuebles, setInmuebles] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [style, setStyle] = useState("default");
  
  // Estados de la API de registro
  const [registrando, setRegistrando] = useState(false);
  const [registroResultado, setRegistroResultado] = useState(null);
  const [registroError, setRegistroError] = useState(null);
  const [showConfirmacion, setShowConfirmacion] = useState(false);
  const [activePopupId, setActivePopupId] = useState(null);
  const [telefonoAfiliado, setTelefonoAfiliado] = useState("");

  const mapRef = useRef(null);

  const selectedStyle = MAP_STYLES[style];
  const is3D = style === "openstreetmap3d";

  // Cargar inmuebles desde la API real o fallback
  useEffect(() => {
    if (!isOpen) return;
    setCargando(true);
    setSeleccionados([]);
    setRegistroResultado(null);
    setRegistroError(null);

    const fetchUrl = apiUrl ? `${apiUrl}/conjuntos_habitacionales` : null;

    if (!fetchUrl) {
      console.warn("API URL no provista. Cargando mock de respaldo.");
      setInmuebles(INMUEBLES_MOCK.map(item => ({ ...item, hasLocation: item.lat !== null && item.lng !== null })));
      setCargando(false);
      return;
    }

    fetch(fetchUrl)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Error HTTP: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.data || []);

        const BASE_LAT = 20.6597;
        const BASE_LNG = -103.3496;

        const mapped = list.map((item, index) => {
          const numId = parseInt(item.id_conjunto_habitacional) || (index + 1);
          const latOffset = Math.sin(numId * 12.9898) * 0.035; 
          const lngOffset = Math.cos(numId * 4.1414) * 0.035;

          const rawLat = item.latitud !== undefined && item.latitud !== null ? item.latitud : item.lat;
          const rawLng = item.longitud !== undefined && item.longitud !== null ? item.longitud : item.lng;

          const parsedLat = parseFloat(rawLat);
          const parsedLng = parseFloat(rawLng);

          const isZeroCoords = parsedLat === 0 && parsedLng === 0;
          const isMissingCoords = isNaN(parsedLat) || isNaN(parsedLng);

          let lat, lng;
          if (isZeroCoords) {
            lat = null;
            lng = null;
          } else if (isMissingCoords) {
            lat = BASE_LAT + latOffset;
            lng = BASE_LNG + lngOffset;
          } else {
            lat = parsedLat;
            lng = parsedLng;
          }

          const precio = item.precio_hasta || item.precio_desde || 5000;

          return {
            id: item.id_conjunto_habitacional || String(numId),
            nombre: item.nombre || `Conjunto ${item.clave_inmueble || numId}`,
            direccion: item.domicilio || "Dirección no especificada",
            tipo: "Conjunto",
            precio_renta: precio,
            precio_desde: item.precio_desde,
            precio_hasta: item.precio_hasta,
            habitaciones: item.cantidad_recamaras || 2,
            superficie: 85,
            lat,
            lng,
            hasLocation: lat !== null && lng !== null,
            disponible: true,
            raw: item
          };
        });

        setInmuebles(mapped);
        setCargando(false);
      })
      .catch((err) => {
        console.error("Error cargando conjuntos_habitacionales, usando mock de respaldo:", err);
        setInmuebles(INMUEBLES_MOCK.map(item => ({ ...item, hasLocation: item.lat !== null && item.lng !== null })));
        setCargando(false);
      });
  }, [isOpen, apiUrl]);

  // Manejar cambio 3D/pitch
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.easeTo({ pitch: is3D ? 60 : 0, duration: 500 });
    }
  }, [is3D]);

  // Volar al último inmueble seleccionado
  useEffect(() => {
    const ultimo = seleccionados[seleccionados.length - 1];
    if (ultimo?.lat && ultimo?.lng && mapRef.current) {
      mapRef.current.flyTo({
        center: [ultimo.lng, ultimo.lat],
        zoom: 15,
        duration: 800,
      });
    }
  }, [seleccionados]);

  // Enviar los 3 conjuntos seleccionados a la API
  const handleConfirmarRegistro = () => {
    if (seleccionados.length !== 3) return;
    setRegistrando(true);
    setRegistroError(null);

    // Obtener correo del localStorage si la prop viene vacía
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const email = correoElectronico || storedUser?.email || "afiliado@ipejal.gob.mx";

    const rawCodigo = String(codigoAfiliado || storedUser?.username || "");
    const cleanCodigo = (typeof rawCodigo === "string" && rawCodigo.startsWith("deudo-"))
      ? (rawCodigo.split("-")[1] || rawCodigo)
      : rawCodigo;

    const payload = {
      codigo_afiliado: String(cleanCodigo),
      correo_afiliado: email,
      telefono_afiliado: `+52${telefonoAfiliado}`,
      id_conjunto_habitacional_a: parseInt(seleccionados[0].id) || seleccionados[0].id,
      id_conjunto_habitacional_b: parseInt(seleccionados[1].id) || seleccionados[1].id,
      id_conjunto_habitacional_c: parseInt(seleccionados[2].id) || seleccionados[2].id,
      folio_tramite: folio ? `${folio}R` : ""
    };

    fetch(`${apiUrl}/tramites_insertar`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const isJson = res.headers.get("content-type")?.includes("application/json");
        const data = isJson ? await res.json() : null;

        if (!res.ok || (data && data.ok === false)) {
          let errorMsg = "Error al crear el trámite.";
          if (data) {
            if (Array.isArray(data.data) && data.data[0]?.error) {
              errorMsg = data.data[0].error;
            } else if (data.message) {
              errorMsg = data.message;
            }
          }
          throw new Error(errorMsg);
        }
        return data;
      })
      .then((resData) => {
        setRegistroResultado(resData);
        setRegistrando(false);
        if (onSelect) {
          onSelect(seleccionados);
        }
      })
      .catch((err) => {
        console.error("Error al registrar en lista de espera:", err);
        setRegistroError(err.message || "Error de red al conectar con el servidor.");
        setRegistrando(false);
      });
  };

  const handleVerInmueble = (inmueble) => {
    handleLocateInmueble(inmueble);
    setActivePopupId(inmueble.id);
    
    // Desplazar en el panel izquierdo
    setTimeout(() => {
      const cardElement = document.getElementById(`inmueble-card-${inmueble.id}`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 50);
  };

  const handleClickInmueble = (inmueble) => {
    if (seleccionados.some(item => item.id === inmueble.id)) {
      // Si ya está seleccionado, enfocamos, abrimos popup y hacemos scroll
      handleVerInmueble(inmueble);
    } else {
      // Agregar si hay menos de 3
      if (seleccionados.length < 3) {
        setSeleccionados([...seleccionados, inmueble]);
        handleVerInmueble(inmueble);
      } else {
        alert("Ya has seleccionado tus 3 prioridades. Deselecciona una para elegir otra.");
      }
    }
  };

  const handleDeseleccionarIndex = (idx) => {
    setSeleccionados(seleccionados.filter((_, i) => i !== idx));
  };

  const handleLocateInmueble = (inmueble) => {
    if (inmueble?.lat && inmueble?.lng && mapRef.current) {
      mapRef.current.flyTo({
        center: [inmueble.lng, inmueble.lat],
        zoom: 15,
        duration: 800,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex p-3 md:p-6"
        style={{ background: "rgba(15, 23, 42, 0.75)" }}
      >
        {/* Estilos para animación del marcador */}
        <style>{`
          @keyframes pulse-emerald {
            0%, 100% { box-shadow: 0 0 0 0 rgba(5,150,105,0.4); }
            50% { box-shadow: 0 0 0 12px rgba(5,150,105,0); }
          }
          .animate-pulse-emerald {
            animation: pulse-emerald 1.5s infinite;
          }
        `}</style>

        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative flex flex-col md:flex-row w-full h-full bg-white rounded-2xl overflow-hidden shadow-2xl"
        >
          {/* Panel lateral */}
          <div className="w-full md:w-96 h-[380px] md:h-full flex-shrink-0 flex flex-col bg-white border-b md:border-b-0 md:border-r border-slate-200 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="size-9 bg-emerald-600 rounded-xl flex items-center justify-center shadow-md shadow-emerald-600/20">
                    <FiMapPin className="text-white size-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-extrabold text-gray-900 tracking-tight">
                      Conjuntos Habitacionales
                    </h2>
                    <p className="text-[10px] text-gray-500 font-medium">
                      {inmuebles.length} conjuntos registrados
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="size-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all active:scale-95"
                >
                  <FiX className="size-4" />
                </button>
              </div>
            </div>

            {/* Lista */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {cargando ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-600" />
                  <p className="text-xs text-gray-400 font-medium">Cargando conjuntos...</p>
                </div>
              ) : inmuebles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                  <FiHome className="size-10 text-gray-300" />
                  <p className="text-sm text-gray-400 font-medium">No hay conjuntos registrados</p>
                </div>
              ) : (
                inmuebles.map((inmueble) => {
                  const selIndex = seleccionados.findIndex(item => item.id === inmueble.id);
                  const isSel = selIndex !== -1;
                  
                  return (
                    <div
                      key={inmueble.id}
                      id={`inmueble-card-${inmueble.id}`}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                        isSel
                          ? "border-emerald-500 bg-emerald-50/30 shadow-md shadow-emerald-500/5"
                          : activePopupId === inmueble.id
                            ? "border-blue-500 bg-blue-50/30 shadow-md shadow-blue-500/5 animate-pulse"
                            : "border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-md bg-purple-100 text-purple-700">
                              Lista de Espera
                            </span>
                            <span className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
                              {inmueble.tipo}
                            </span>
                          </div>
                          <h3 className="text-sm font-bold text-gray-900 truncate">{inmueble.nombre}</h3>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{inmueble.direccion}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <FiHome className="size-3" />
                              {inmueble.habitaciones} hab
                            </span>
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <FiMaximize2 className="size-3" />
                              {inmueble.superficie}m²
                            </span>
                            <span className="flex items-center gap-1 text-xs font-bold text-emerald-700">
                              {formatPriceRange(inmueble.precio_desde, inmueble.precio_hasta, inmueble.precio_renta)}
                            </span>
                          </div>

                          {/* Botones de acción dentro del Div */}
                          <div className="flex gap-2 mt-3.5 pt-3 border-t border-slate-100">
                            <button
                              type="button"
                              onClick={() => inmueble.hasLocation && handleVerInmueble(inmueble)}
                              disabled={!inmueble.hasLocation}
                              className={`flex-1 flex items-center justify-center gap-1 h-8 rounded-lg border text-[11px] font-bold transition-all ${
                                inmueble.hasLocation
                                  ? "bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200 active:scale-[0.98]"
                                  : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-50"
                              }`}
                              title={inmueble.hasLocation ? "Ubicar en el mapa" : "Sin ubicación en el mapa"}
                            >
                              <FiEye className="size-3.5" />
                              <span>Ver</span>
                            </button>
                            
                            {isSel ? (
                              <button
                                type="button"
                                disabled={true}
                                className="flex-1 flex items-center justify-center gap-1 h-8 bg-emerald-100 text-emerald-700 rounded-lg border border-emerald-200 text-[11px] font-bold cursor-not-allowed"
                              >
                                <FiCheck className="size-3.5" />
                                <span>{selIndex + 1}ª Opción</span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleClickInmueble(inmueble)}
                                disabled={seleccionados.length >= 3}
                                className={`flex-1 flex items-center justify-center gap-1 h-8 rounded-lg text-[11px] font-bold transition-all active:scale-[0.98] ${
                                  seleccionados.length >= 3
                                    ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
                                    : "bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-600 shadow-sm"
                                }`}
                              >
                                <FiCheck className="size-3.5" />
                                <span>Seleccionar</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer con las 3 prioridades */}
            <div className="p-4 border-t border-slate-100 bg-slate-50/50 space-y-3">
              <div className="space-y-1.5">
                <p className="text-[9px] uppercase font-extrabold text-gray-400 tracking-widest">Prioridades de Selección</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {[0, 1, 2].map((idx) => {
                    const item = seleccionados[idx];
                    return item ? (
                      <div 
                        key={idx} 
                        className="p-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-center w-full flex flex-col justify-between items-center min-h-[72px]"
                      >
                        <p className="text-[8px] font-extrabold uppercase tracking-wider text-emerald-600">{idx + 1}ª Opción</p>
                        <p className="text-[10px] font-black truncate mt-0.5 w-full text-slate-800">{item.nombre}</p>
                        
                        {/* Fila de acciones (Ver en mapa / Quitar) */}
                        <div className="flex items-center justify-center gap-1.5 mt-2 w-full">
                          <button
                            type="button"
                            onClick={() => item.hasLocation && handleVerInmueble(item)}
                            disabled={!item.hasLocation}
                            className={`flex-1 h-6 py-0.5 rounded-md flex items-center justify-center shadow-sm transition-all text-[9px] font-bold gap-1 ${
                              item.hasLocation
                                ? "bg-white hover:bg-emerald-50 text-emerald-600 hover:text-emerald-700 border border-emerald-100 active:scale-90"
                                : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-50"
                            }`}
                            title={item.hasLocation ? "Ver en el mapa" : "Sin ubicación en el mapa"}
                          >
                            <FiEye className="size-3" />
                            <span>Ver</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeseleccionarIndex(idx)}
                            className="h-6 px-2 py-0.5 bg-white hover:bg-red-50 text-slate-400 hover:text-red-600 border border-slate-200 rounded-md flex items-center justify-center shadow-sm transition-all active:scale-90 text-[9px] font-bold"
                            title="Quitar opción"
                          >
                            <FiX className="size-3" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        key={idx} 
                        className="p-2 rounded-lg border border-slate-200 bg-white text-slate-400 text-center"
                      >
                        <p className="text-[8px] font-extrabold uppercase tracking-wider text-gray-400">{idx + 1}ª Opción</p>
                        <p className="text-[10px] font-bold truncate mt-0.5">Vacío</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <button
                onClick={() => setShowConfirmacion(true)}
                disabled={seleccionados.length !== 3}
                className={`w-full flex items-center justify-center gap-2 h-12 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-all active:scale-[0.98] ${
                  seleccionados.length === 3
                    ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-600/20"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                <FiCheck className="size-4" />
                {seleccionados.length === 3 
                  ? "Registrar Lista de Espera" 
                  : `Selecciona ${3 - seleccionados.length} conjuntos más`}
              </button>
            </div>
          </div>

          {/* Mapa MapLibre GL */}
          <div className="flex-1 relative min-h-[250px] md:min-h-[400px]">
            {!cargando && (
              <Map
                ref={mapRef}
                center={[-103.3496, 20.6597]}
                zoom={12}
                styles={
                  selectedStyle
                    ? { light: selectedStyle, dark: selectedStyle }
                    : undefined
                }
                className="w-full h-full"
              >
                <MapControls showZoom={true} showCompass={true} showLocate={true} showFullscreen={true} />

                {/* Burbuja de información programática global */}
                {activePopupId && (() => {
                  const activeInmueble = inmuebles.find(item => item.id === activePopupId);
                  if (!activeInmueble || !activeInmueble.hasLocation) return null;
                  return (
                    <MapPopup
                      longitude={activeInmueble.lng}
                      latitude={activeInmueble.lat}
                      onClose={() => setActivePopupId(null)}
                      closeButton={true}
                    >
                      <div className="min-w-[180px] text-xs">
                        <strong className="text-sm block mb-1 text-slate-900">{activeInmueble.nombre}</strong>
                        <span className="text-slate-500 block mb-1">{activeInmueble.direccion}</span>
                        <span className="font-bold text-emerald-700 block mt-1">
                          {formatPriceRange(activeInmueble.precio_desde, activeInmueble.precio_hasta, activeInmueble.precio_renta)} / mes
                        </span>
                      </div>
                    </MapPopup>
                  );
                })()}

                {/* Marcadores */}
                {inmuebles
                  .filter((inmueble) => inmueble.hasLocation)
                  .map((inmueble) => {
                    const selIndex = seleccionados.findIndex(item => item.id === inmueble.id);
                    const isSel = selIndex !== -1;

                    return (
                      <MapMarker
                        key={inmueble.id}
                        longitude={inmueble.lng}
                        latitude={inmueble.lat}
                        onClick={() => handleClickInmueble(inmueble)}
                      >
                        <MarkerContent>
                        {isSel ? (
                          // Seleccionado
                          <div className="w-[44px] h-[44px] bg-emerald-600 border-4 border-emerald-200 rounded-full flex items-center justify-center shadow-lg cursor-pointer animate-pulse-emerald text-white font-extrabold text-sm">
                            {selIndex + 1}
                          </div>
                        ) : (
                          // Disponible (No seleccionado)
                          <div className="w-[36px] h-[36px] bg-blue-600 border-3 border-white rounded-full flex items-center justify-center shadow-md cursor-pointer hover:scale-110 hover:bg-blue-700 transition-all">
                            <FiHome className="text-white size-4" />
                          </div>
                        )}

                        {/* Nombre permanente del inmueble visible sin seleccionar */}
                        <MarkerLabel position="bottom">
                          <span className="bg-white/95 text-slate-800 text-[10px] font-extrabold px-1.5 py-0.5 rounded shadow-sm border border-slate-200/60 block max-w-[130px] truncate">
                            {inmueble.nombre}
                          </span>
                        </MarkerLabel>
                      </MarkerContent>
                    </MapMarker>
                  );
                })}
              </Map>
            )}

            {/* Selector de estilos en la esquina superior derecha */}
            <div className="absolute top-4 right-14 z-[1000]">
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                className="bg-white text-slate-900 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="default">Default (Carto)</option>
                <option value="openstreetmap">OpenStreetMap</option>
                <option value="openstreetmap3d">OpenStreetMap 3D</option>
              </select>
            </div>
          </div>

          {/* ========================================== */}
          {/* PANTALLAS DE API: CARGANDO, ÉXITO Y ERROR */}
          {/* ========================================== */}

          {/* Cargando / Registrando */}
          {registrando && (
            <div className="absolute inset-0 z-[10000] bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center animate-in fade-in-0 duration-200">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600 mb-4" />
              <h3 className="text-lg font-extrabold text-gray-900">Registrando Solicitud</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-xs">
                Estamos procesando tu registro en la lista de espera de IPEJAL. Por favor espera un momento.
              </p>
            </div>
          )}

          {/* Éxito */}
          {registroResultado && (
            <div className="absolute inset-0 z-[10000] bg-white flex flex-col items-center justify-center p-6 text-center animate-in zoom-in-95 duration-300">
              <div className="size-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6 text-emerald-600 animate-bounce">
                <FiCheck className="text-4xl" />
              </div>
              <h3 className="text-2xl font-extrabold text-gray-900">¡Registro Exitoso!</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-md">
                Has sido registrado en la lista de espera de IPEJAL. Se ha enviado un correo electrónico con los detalles y confirmación de tu registro.
              </p>
              
              {/* Detalles del Folio */}
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 my-6 min-w-[320px] text-left space-y-2.5 shadow-sm">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-400 font-bold uppercase tracking-wider">Folio Asignado</span>
                  <span className="text-emerald-700 font-extrabold text-sm">
                    {registroResultado.data?.folio || registroResultado.folio || "FOL-RENTAS-1029"}
                  </span>
                </div>
                <div className="h-px bg-slate-200" />
                <div className="text-xs space-y-1">
                  <p className="text-gray-400 font-bold uppercase tracking-wider mb-1">Prioridades Seleccionadas:</p>
                  <p className="font-extrabold text-gray-700">1ª Opción: {seleccionados[0]?.nombre}</p>
                  <p className="font-extrabold text-gray-700">2ª Opción: {seleccionados[1]?.nombre}</p>
                  <p className="font-extrabold text-gray-700">3ª Opción: {seleccionados[2]?.nombre}</p>
                </div>
              </div>

              <button
                onClick={() => {
                  setRegistroResultado(null);
                  setSeleccionados([]);
                  onClose();
                }}
                className="px-8 py-3 bg-emerald-600 text-white font-extrabold uppercase tracking-wider text-xs rounded-xl hover:bg-emerald-700 shadow-lg shadow-emerald-600/20 active:scale-95 transition-all"
              >
                Cerrar Ventana
              </button>
            </div>
          )}

          {/* Error */}
          {registroError && (
            <div className="absolute inset-0 z-[10000] bg-white flex flex-col items-center justify-center p-6 text-center animate-in fade-in-0 duration-200">
              <div className="size-16 bg-red-100 rounded-full flex items-center justify-center mb-6 text-red-600">
                <FiX className="text-4xl" />
              </div>
              <h3 className="text-xl font-extrabold text-gray-900">Error en el Registro</h3>
              <p className="text-sm text-red-600 mt-2 max-w-md font-extrabold">
                {registroError}
              </p>
              
              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => {
                    setRegistroError(null);
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-700 font-bold uppercase tracking-wider text-xs rounded-xl hover:bg-gray-200 active:scale-95 transition-all"
                >
                  Modificar Selección
                </button>
                <button
                  onClick={handleConfirmarRegistro}
                  className="px-6 py-3 bg-emerald-600 text-white font-extrabold uppercase tracking-wider text-xs rounded-xl hover:bg-emerald-700 shadow-lg shadow-emerald-600/20 active:scale-95 transition-all"
                >
                  Reintentar Registro
                </button>
              </div>
            </div>
          )}

          {/* Modal de confirmación antes del envío final */}
          <ModalConfirmacion
            isOpen={showConfirmacion}
            onClose={() => {
              setShowConfirmacion(false);
              setTelefonoAfiliado("");
            }}
            onConfirm={handleConfirmarRegistro}
            titulo="Confirmar Lista de Espera"
            mensaje={
              <span className="block text-left space-y-3 text-slate-600">
                <span className="block font-medium">¿Estás seguro de registrar estas 3 opciones de conjuntos habitacionales en la lista de espera?</span>
                {/* Vista Compacta en Móvil */}
                <span className="block sm:hidden bg-slate-50 p-2.5 rounded-xl border border-slate-100 divide-y divide-slate-200/50 mt-1.5">
                  {[0, 1, 2].map((idx) => {
                    const item = seleccionados[idx];
                    if (!item) return null;
                    return (
                      <span key={idx} className="flex justify-between items-center py-1.5 text-[11px] text-slate-700 font-semibold">
                        <span className="truncate mr-2">
                          <span className="font-extrabold text-slate-900">{idx + 1}ª: </span>
                          <span className="text-emerald-700 font-extrabold">{item.nombre}</span>
                        </span>
                        <span className="text-slate-500 font-bold shrink-0">
                          {formatPriceRange(item.precio_desde, item.precio_hasta, item.precio_renta)}
                        </span>
                      </span>
                    );
                  })}
                </span>

                {/* Vista Detallada Grid en Escritorio */}
                <span className="hidden sm:grid sm:grid-cols-3 gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100 mt-2">
                  {[0, 1, 2].map((idx) => {
                    const item = seleccionados[idx];
                    if (!item) return null;
                    return (
                      <span key={idx} className="block text-slate-800 text-xs text-left bg-white p-3 rounded-xl border border-slate-200/60 shadow-sm">
                        <span className="font-extrabold text-slate-900 block">{idx + 1}ª Opción</span>
                        <span className="text-emerald-700 font-extrabold block truncate mt-0.5" title={item.nombre}>{item.nombre}</span>
                        <span className="block text-[10px] text-slate-500 font-semibold mt-1 line-clamp-2 h-7 leading-tight" title={item.direccion}>{item.direccion}</span>
                        <span className="block text-[10px] text-emerald-700 font-extrabold mt-1.5 pt-1.5 border-t border-slate-100">
                          Renta: {formatPriceRange(item.precio_desde, item.precio_hasta, item.precio_renta)} / mes
                        </span>
                      </span>
                    );
                  })}
                </span>
                <span className="block text-[10px] sm:text-[11px] text-amber-600 font-black uppercase tracking-wide sm:tracking-widest pt-1 sm:pt-2">
                  ⚠️ Una vez confirmado, esta acción no se podrá modificar.
                </span>
              </span>
            }
            textoConfirmar="Sí, Registrar"
            textoCancelar="Cancelar"
            tipo="warning"
            showTelefono={true}
            telefonoValue={telefonoAfiliado}
            onTelefonoChange={setTelefonoAfiliado}
            maxWidthClass="sm:max-w-2xl"
            checkboxText="La presente solicitud tiene una vigencia de 6 meses. Favor de renovar la documentación mientras no se le asigne departamento, en caso de no renovar en un termino no mayor a 7 días posteriores al termino de su vigencia será desechada y de así requerirlo podrá iniciar de nuevo su tramite."
          />

        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
